"use client";

import { useState } from "react";

import { cn } from "@/src/lib/utils";
import { Text } from "@/src/components/ui/Typography";
import { FacebookIcon, LinkedInIcon, LinkIcon, PinterestIcon, XIcon } from "./icons";

const BASE_ICON_CLASS = "inline-flex items-center justify-center text-neutral-500 transition-colors duration-200";

interface ShareButtonsProps {
  title: string;
  url: string;
  coverImage?: string;
}

export function ShareButtons({ title, url, coverImage }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked - ignore */
    }
  };

  const platforms = [
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      Icon: XIcon,
      hoverColor: "hover:text-(--color-brand-x)",
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      Icon: FacebookIcon,
      hoverColor: "hover:text-(--color-brand-facebook)",
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      Icon: LinkedInIcon,
      hoverColor: "hover:text-(--color-brand-linkedin)",
    },
    {
      name: "Pinterest",
      href: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(coverImage ?? "")}&description=${encodeURIComponent(title)}`,
      Icon: PinterestIcon,
      hoverColor: "hover:text-(--color-brand-pinterest)",
    },
  ] as const;

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <div aria-hidden className="tracking-[0.8em] text-neutral-700 text-xl">
        * * *
      </div>

      <div className="flex items-center gap-6">
        {platforms.map(({ name, href, Icon, hoverColor }) => (
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${name}`}
            className={cn(BASE_ICON_CLASS, hoverColor)}
          >
            <Icon />
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          aria-label="Copy link"
          className={cn(BASE_ICON_CLASS, "cursor-pointer hover:text-brand")}
        >
          <LinkIcon />
        </button>
      </div>

      {copied && (
        <Text size="labelSM" className="text-success">
          Copied!
        </Text>
      )}
    </div>
  );
}
